/**
 * @file     webThemeState.ts
 * @readme   ./README.md
 * @purpose  Web-only theme boot utilities: reads/writes theme preference from localStorage, applies data-theme attributes to the HTML element, generates the inline boot script and critical CSS to prevent flash-of-wrong-theme.
 */
import { createDesignTheme } from '@/design-system/theme';
import {
  SEMANTIC_CSS_VARS,
  SEMANTIC_ROLE_CSS_VARS,
  createRawSemanticRoleColors,
} from '@/design-system/tokens/semanticColors';
import type { ThemeMode, ThemePreference, SemanticColorTokens } from '@/core/types';
import type { StorageDriver } from '@/core/utils/browserRuntime';
import { createGlassClassCss, createGlassCssVars } from '@/design-system/tokens/glass';
import { parallaxBackground } from './parallaxBackground';

export const THEME_PREFERENCE_STORAGE_KEY = 'app-theme-preference';
export const THEME_ATTRIBUTE = 'data-theme';
export const THEME_PREFERENCE_ATTRIBUTE = 'data-theme-preference';
export const THEME_READY_ATTRIBUTE = 'data-theme-ready';
export const THEME_HYDRATED_ATTRIBUTE = 'data-theme-hydrated';
/** Marks a page whose server-rendered first paint follows the theme via CSS variables. */
export const THEME_SSR_SAFE_ATTRIBUTE = 'data-theme-ssr-safe';
export const THEME_STATE_WINDOW_KEY = '__RELTA_THEME__';
export const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const darkTheme = createDesignTheme('dark');
const lightTheme = createDesignTheme('light');

export const THEME_META_COLORS: Record<ThemeMode, string> = {
  dark: darkTheme.rawSemanticColors.backgroundPrimary,
  light: lightTheme.rawSemanticColors.backgroundPrimary,
};

const themeByMode: Record<ThemeMode, typeof darkTheme> = {
  dark: darkTheme,
  light: lightTheme,
};

type ThemeBootState = {
  mode: ThemeMode;
  preference: ThemePreference;
};

type ThemeStateDocument = {
  documentElement: {
    dataset: Record<string, string | undefined>;
    style: {
      colorScheme: string;
    };
    setAttribute?: (name: string, value: string) => void;
  };
  querySelector: (selector: string) => {
    setAttribute: (name: string, value: string) => void;
  } | null;
};

type ThemeStateWindow = {
  localStorage?: StorageDriver;
  matchMedia?: (query: string) => {
    matches: boolean;
  };
  [THEME_STATE_WINDOW_KEY]?: Partial<ThemeBootState>;
};

export const normalizeThemeMode = (value: unknown): ThemeMode | null => {
  return value === 'light' || value === 'dark' ? value : null;
};

export const normalizeThemePreference = (
  value: unknown,
): ThemePreference | null => {
  return value === 'system' ? 'system' : normalizeThemeMode(value);
};

export const resolveThemeMode = ({
  preference,
  systemMode,
  fallbackMode,
}: {
  preference?: ThemePreference | null;
  systemMode?: ThemeMode | null;
  fallbackMode: ThemeMode;
}): ThemeMode => {
  const normalizedPreference = normalizeThemePreference(preference);

  if (normalizedPreference === 'light' || normalizedPreference === 'dark') {
    return normalizedPreference;
  }

  return normalizeThemeMode(systemMode) ?? fallbackMode;
};

export const readStoredThemePreference = (
  storage: StorageDriver | null,
): ThemePreference | null => {
  if (!storage) {
    return null;
  }

  try {
    return normalizeThemePreference(
      storage.getItem(THEME_PREFERENCE_STORAGE_KEY),
    );
  } catch {
    return null;
  }
};

export const writeStoredThemePreference = (
  storage: StorageDriver | null,
  preference: ThemePreference,
) => {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(THEME_PREFERENCE_STORAGE_KEY, preference);
  } catch {
    // Ignore storage failures and keep runtime state only.
  }

  // Mirror to cookie so the server can SSR with the correct theme.
  try {
    if (typeof document !== 'undefined') {
      document.cookie = `${THEME_PREFERENCE_STORAGE_KEY}=${preference};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
    }
  } catch {
    // Cookie write failures are non-critical.
  }
};

export const getSystemThemeMode = (
  matchesDarkMode: boolean | null | undefined,
): ThemeMode | null => {
  if (typeof matchesDarkMode !== 'boolean') {
    return null;
  }

  return matchesDarkMode ? 'dark' : 'light';
};

const readBootStateFromWindow = (
  windowObject: ThemeStateWindow | null | undefined,
): ThemeBootState | null => {
  if (!windowObject) {
    return null;
  }

  const candidate = windowObject[THEME_STATE_WINDOW_KEY];

  if (!candidate) {
    return null;
  }

  const mode = normalizeThemeMode(candidate.mode);
  const preference = normalizeThemePreference(candidate.preference);

  if (!mode || !preference) {
    return null;
  }

  return {
    mode,
    preference,
  };
};

export const getBootThemeState = (
  fallbackMode: ThemeMode,
  options: {
    document?: ThemeStateDocument | null;
    window?: ThemeStateWindow | null;
  } = {},
): ThemeBootState => {
  const documentObject =
    options.document ?? (typeof document === 'undefined' ? null : (document as unknown as ThemeStateDocument));
  const windowObject =
    options.window ?? (typeof window === 'undefined' ? null : (window as unknown as ThemeStateWindow));

  const bootState = readBootStateFromWindow(windowObject);

  if (bootState) {
    return bootState;
  }

  const documentMode = normalizeThemeMode(
    documentObject?.documentElement?.dataset?.theme,
  );
  const documentPreference = normalizeThemePreference(
    documentObject?.documentElement?.dataset?.themePreference,
  );

  if (documentMode && documentPreference) {
    return {
      mode: documentMode,
      preference: documentPreference,
    };
  }

  const storedPreference =
    readStoredThemePreference(windowObject?.localStorage ?? null) ?? 'system';
  const systemMode = getSystemThemeMode(
    windowObject?.matchMedia?.(COLOR_SCHEME_QUERY).matches,
  );

  return {
    preference: storedPreference,
    mode: resolveThemeMode({
      preference: storedPreference,
      systemMode,
      fallbackMode,
    }),
  };
};

export const applyDocumentThemeState = (
  documentObject: ThemeStateDocument | null | undefined,
  state: ThemeBootState & { ready?: boolean },
) => {
  if (!documentObject) {
    return;
  }

  const { documentElement } = documentObject;

  if (typeof documentElement.setAttribute === 'function') {
    documentElement.setAttribute(THEME_ATTRIBUTE, state.mode);
    documentElement.setAttribute(THEME_PREFERENCE_ATTRIBUTE, state.preference);
    documentElement.setAttribute(
      THEME_READY_ATTRIBUTE,
      state.ready === false ? 'false' : 'true',
    );
  } else {
    documentElement.dataset.theme = state.mode;
    documentElement.dataset.themePreference = state.preference;
    documentElement.dataset.themeReady = state.ready === false ? 'false' : 'true';
  }

  documentElement.style.colorScheme = state.mode;
  const themeMetaTag = documentObject.querySelector('meta[name="theme-color"]');
  themeMetaTag?.setAttribute('content', THEME_META_COLORS[state.mode]);
};

export const createThemeBootScript = (fallbackMode: ThemeMode) => {
  const scriptSource = `
    (function () {
      var fallbackMode = ${JSON.stringify(fallbackMode)};
      var preferenceKey = ${JSON.stringify(THEME_PREFERENCE_STORAGE_KEY)};
      var themeAttribute = ${JSON.stringify(THEME_ATTRIBUTE)};
      var preferenceAttribute = ${JSON.stringify(THEME_PREFERENCE_ATTRIBUTE)};
      var readyAttribute = ${JSON.stringify(THEME_READY_ATTRIBUTE)};
      var stateKey = ${JSON.stringify(THEME_STATE_WINDOW_KEY)};
      var colorSchemeQuery = ${JSON.stringify(COLOR_SCHEME_QUERY)};
      var themeMetaColors = ${JSON.stringify(THEME_META_COLORS)};

      var normalizeMode = function (value) {
        return value === 'light' || value === 'dark' ? value : null;
      };

      var normalizePreference = function (value) {
        return value === 'system' ? 'system' : normalizeMode(value);
      };

      var resolveMode = function (preference, systemMode) {
        var normalizedPreference = normalizePreference(preference);
        if (normalizedPreference === 'light' || normalizedPreference === 'dark') {
          return normalizedPreference;
        }

        return normalizeMode(systemMode) || fallbackMode;
      };

      try {
        var storage = null;
        try {
          storage = window.localStorage;
        } catch (error) {}

        var storedPreference = null;
        try {
          storedPreference = storage ? normalizePreference(storage.getItem(preferenceKey)) : null;
        } catch (error) {}

        var mediaQuery = typeof window.matchMedia === 'function'
          ? window.matchMedia(colorSchemeQuery)
          : null;
        var systemMode = mediaQuery ? (mediaQuery.matches ? 'dark' : 'light') : null;
        var preference = storedPreference || 'system';
        var mode = resolveMode(preference, systemMode);
        var root = document.documentElement;
        var readyState = 'true';

        root.setAttribute(themeAttribute, mode);
        root.setAttribute(preferenceAttribute, preference);
        root.setAttribute(readyAttribute, readyState);
        root.style.colorScheme = mode;

        var metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
          metaThemeColor.setAttribute('content', themeMetaColors[mode]);
        }

        /* Glass lens: backdrop-filter url() refracts only in Chromium; WebKit and Gecko paint nothing, so gate on the brand. */
        var brands = navigator.userAgentData && navigator.userAgentData.brands;
        if (brands && brands.some(function (b) { return b.brand === 'Chromium'; })) {
          document.documentElement.setAttribute('data-glass-lens', '');
        }

        window[stateKey] = {
          mode: mode,
          preference: preference
        };

        try {
          document.cookie = preferenceKey + '=' + preference + ';path=/;max-age=31536000;SameSite=Lax';
        } catch (e) {}
      } catch (error) {}
    })();
  `;

  return scriptSource.replace(/\s+/g, ' ').trim();
};

export const createThemeBootStyles = () => {
  // Critical CSS: define ALL semantic color CSS custom properties per theme.
  // Boot script sets data-theme before paint; these rules activate the correct
  // variable values instantly, no flash even before React hydrates.
  const cssVarKeys = Object.keys(SEMANTIC_CSS_VARS) as (keyof SemanticColorTokens)[];

  const criticalColorStyles = (Object.keys(themeByMode) as ThemeMode[])
    .map((mode) => {
      const theme = themeByMode[mode];
      const raw = theme.rawSemanticColors;
      const rawColors = theme.rawColors;
      const varDeclarations = cssVarKeys
        .map((key) => `${SEMANTIC_CSS_VARS[key]}:${raw[key]}`)
        .join(';');
      const rawRoles = createRawSemanticRoleColors(mode, theme.palette);
      const roleDeclarations = (Object.keys(SEMANTIC_ROLE_CSS_VARS) as (keyof typeof SEMANTIC_ROLE_CSS_VARS)[])
        .map((key) => `${SEMANTIC_ROLE_CSS_VARS[key]}:${rawRoles[key]}`)
        .join(';');
      const extraVars = [
        `--r-hover-surface:${rawColors.hoverSurface}`,
        `--r-hover-elevated:${rawColors.hoverElevated}`,
        `--r-pressed-surface:${rawColors.pressedSurface}`,
        `--r-accent-yellow:${rawColors.accentYellow}`,
        `--r-accent-yellow-muted:${rawColors.accentYellowMuted}`,
        // Accent + primary scale steps the semantic set has no name for: the
        // eyebrow/link colour on a dark surface, the hairline that tints a
        // pill or an active input, and the 2px rule above a step column.
        // Exposed as vars so Emotion-free CSS modules can reach them without
        // hardcoding a hex per theme.
        `--r-accent-strong:${theme.palette.accent[700]}`,
        `--r-accent-line:${theme.palette.accent[200]}`,
        `--r-primary-line:${theme.palette.primary[400]}`,
        `--r-parallax-bg:${parallaxBackground(theme.palette)}`,
      ].join(';');
      const glassVars = createGlassCssVars(mode, {
        surface: theme.palette.semantic.surface,
        textPrimary: theme.palette.semantic.textPrimary,
        contrastEdge: theme.palette.semantic.divider,
        inverse: mode === 'dark' ? theme.palette.neutral[700] : theme.palette.semantic.textPrimary,
      });
      return `html[${THEME_ATTRIBUTE}="${mode}"]{${varDeclarations};${roleDeclarations};${extraVars};${glassVars};background:var(--r-bg);color-scheme:${mode};color:var(--r-text)}`;
    })
    .join('');

  const readyStateStyles = `html:not([${THEME_READY_ATTRIBUTE}="true"]) body *{transition:none!important;}`;

  // The server always renders light (the root layout reads no cookie, so pages
  // can be cached). Component styles that use raw palette values stay light
  // until React applies the stored theme, which reads as white-on-white on a
  // dark page. A dark visitor sees nothing for that moment instead; the timed
  // reveal guarantees content even if scripts never load. Pages whose first
  // paint is verified theme-safe (CSS variables only) render a
  // ThemeSsrSafe marker and are painted immediately.
  const darkHydrationStyles = `html[${THEME_ATTRIBUTE}="dark"]:not([${THEME_HYDRATED_ATTRIBUTE}]) body:not(:has([${THEME_SSR_SAFE_ATTRIBUTE}])){opacity:0;animation:r-theme-reveal 0s linear 1.2s forwards;}@keyframes r-theme-reveal{to{opacity:1}}`;

  return `${criticalColorStyles}${readyStateStyles}${darkHydrationStyles}${createGlassClassCss()}`;
};
